import {
  MdCategory,
  MdGasMeter,
  MdPeopleAlt,
  MdViewList,
} from "react-icons/md";
import Widget from "views/rtl/default/components/Widget";
import { useQuery } from "@tanstack/react-query";
import { reqFunction } from "utils/API";
import { ChartOptions } from "views/systemReports/ProjecsReport";
import PieChart from "components/charts/PieChart";
import DonutChart from "components/charts/DonutChart";
import MultiColumnsChart from "components/charts/MultiColumnsChart";
import { DynamicOption } from "components/fields/SelectInput";
import { useEffect, useMemo, useState } from "react";
import { getToday } from "utils/TimeUtiles";
import { counterTagSelect } from "variables";
import { ProjectObject } from "views/projects";
import { UserObject } from "views/users/UsersTable";
import { TypeObject } from "views/settings/DeviceTypesTable";
import MixChart from "components/charts/MixChart";
import ConsumptionChartFilter from "views/systemReports/ConsumptionChartFilter";
import WaterMetersPicker, {
  MeterOption,
} from "../../projectInformation/WaterMetersPicker";
import CustomMap from "components/map";
import Loading from "components/loading";
import {
  renderComplexChartData,
  renderComplexChartOptions,
  renderToast,
} from "utils/globalUtils";
import { renderSelectData } from "utils/CommonFunctions";

const Dashboard = () => {
  const lastSuccess = localStorage.getItem("lastSuccess");
  if (lastSuccess) {
    renderToast(`آخرین ورود موفق: ${lastSuccess}`, "info");
    localStorage.removeItem("lastSuccess");
  }

  const lastFail = localStorage.getItem("lastFail");
  if (lastFail) {
    renderToast(`آخرین تلاش ناموفق: ${lastFail}`, "warn");
    localStorage.removeItem("lastFail");
  }

  const [tag, setTag] = useState<DynamicOption>(counterTagSelect[0]);
  const [user, setUser] = useState<DynamicOption>(null);
  const [project, setProject] = useState<DynamicOption>(null);
  const [type, setType] = useState<DynamicOption>(null);
  const fromDate = getToday().jDate(1);
  const tillDate = getToday().add(1, "day");

  const [metersOpen, setMetersOpen] = useState(false);
  const [selectedMeters, setSelectedMeters] = useState<string[]>([]);

  let permissions: string[] = JSON.parse(
    window.localStorage.getItem("permissions")
  );

  useEffect(() => {
    if (selectedMeters.length > 0) {
      setUser(null);
      setProject(null);
    }
  }, [selectedMeters.length]);

  const projectId = project?.value ?? null;
  const {
    data: metersData,
    isLoading: metersLoading,
    status: metersStatus,
  } = useQuery({
    enabled: metersOpen,
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getAll",
        {
          page: 1,
          count: 1000,
          user_id: null,
          project_id: projectId,
          water_meter_serial: null,
          water_meter_tag_id: null,
        },
        "post"
      ),
    queryKey: ["meters", "byProject", projectId],
  });

  const meterOptions: MeterOption[] = useMemo(() => {
    const arr = metersData?.data ?? [];
    return arr.map((m: any) => ({
      value: m.water_meter_serial,
      label: m.water_meter_name || m.water_meter_serial,
      meta: m,
    }));
  }, [metersData]);

  const {
    data: dashboardData,
    isLoading: dashboardDataIsLoading,
    status: dashboardDataStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("watermeters/admin/countAll", {
        type: null,
        project: null,
        water_meter_activation: null,
        water_meter_validation: null,
      }),
    queryKey: ["dashboardStatistics"],
  });

  const {
    data: citiesData,
    isLoading: citiesIsLoading,
    status: citiesStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("WaterMeterProjectsURL/admin/getAll/city/count", {}, "get"),
    queryKey: ["citiesStatistics"],
  });

  const {
    data: devicesData,
    isLoading: devicesIsLoading,
    status: devicesStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("WaterMeterTags/admin/total/statistcs", {}, "get"),
    queryKey: ["devicesStatistics"],
  });

  const {
    data: typesConditionData,
    isLoading: typesConditionIsLoading,
    status: typesConditionStatus,
  } = useQuery({
    queryFn: () => reqFunction("WaterMeterTypes/admin/getAllValues", {}, "get"),
    queryKey: ["devicesTypeConditions"],
  });

  const {
    data: usersData,
    isLoading: usersIsLoading,
    status: usersStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("users/admin/getAllUsers", {
        page: 1,
        count: 100,
        user_name: "",
        user_lastname: "",
        user_phone: "",
      }),
    queryKey: ["usersList"],
  });

  const {
    data: projectsData,
    isLoading: projectsIsLoading,
    status: projectsStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("WaterMeterProjectsURL/admin/getAll", {
        page: 1,
        count: 100,
        water_meter_project_name: null,
        water_meter_project_create_date: null,
        user_id: null,
      }),
    queryKey: ["projectList"],
  });

  const {
    data: typesData,
    isLoading: typesIsLoading,
    status: typesStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("WaterMeterTypes/admin/getAll", {
        page: 1,
        count: 10,
        water_meter_type_name: "",
        water_meter_type_create_date: "",
      }),
    queryKey: ["typesList"],
  });

  const waterMetersParam = selectedMeters.length ? selectedMeters : null;

  const {
    data: consumptionsDatesData,
    isLoading: consumptionsDatesIsLoading,
    status: consumptionsDatesStatus,
    refetch: consumptionDatesRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getAll/consumption/date",
        {
          page: 1,
          count: 1000,
          project_id: project ? project.value : null,
          user_id: user ? user.value : null,
          water_meters: waterMetersParam,
          type_id: type ? type.value : null,
          tag_id: tag.value,
          start_time: fromDate.format("YYYY-M-D"),
          end_time: tillDate.format("YYYY-M-D"),
        },
        "post"
      ),
    queryKey: [
      "consumptionsDates",
      project,
      user,
      tag,
      type,
      JSON.stringify(waterMetersParam),
    ],
  });

  const renderDonutData = (data: any) => {
    const chartData: string[] = [];
    delete data?.all_water_meters_count;
    for (const property in data) {
      chartData.push(data[property]);
    }
    return chartData;
  };
  const renderDonutOptions = (data: any) => {
    const chartOptions: ChartOptions = { labels: [] };
    delete data?.all_water_meters_count;
    for (const property in data) {
      chartOptions.labels.push(property);
    }
    return chartOptions;
  };
  const renderChartData = (data: any) => {
    const chartData: string[] = [];
    for (const property in data) {
      chartData.push(data[property]);
    }
    return chartData;
  };
  const renderChartOptions = (data: any) => {
    const chartOptions: ChartOptions = { labels: [] };
    for (const property in data) {
      chartOptions.labels.push(property);
    }
    return chartOptions;
  };
  const renderSaleCharData = (data: any) => {
    let chartData: { name: string; data: number[] }[] = [];
    const saleData: number[] = [];
    const availableData: number[] = [];
    for (const property in data) {
      saleData.push(data[property].activation);
      availableData.push(data[property].all_water_meter);
    }
    chartData = [
      { name: "فروش رفته", data: saleData },
      { name: "موجودی کل", data: availableData },
    ];
    return chartData;
  };
  const renderSaleChartOptions = (data: any) => {
    const SaleChartOptions: string[] = [];
    for (const property in typesConditionData.data) {
      SaleChartOptions.push(property);
    }
    return SaleChartOptions;
  };
  const renderTypeSelect = (typeList: TypeObject[]) => {
    let typeSelect: DynamicOption[] = [];
    let typeInProject: string[] = [];
    if (project) {
      let fP: ProjectObject[] = projectsData.data.filter(
        (p: ProjectObject) => p.water_meter_project_id === project.value
      );
      fP[0].types.forEach((type) =>
        typeInProject.push(type.water_meter_type_id)
      );
    }
    typeList
      .filter((types) => types.water_meter_tag.water_meter_tag_id === tag.value)
      .forEach((type) => {
        typeSelect.push({
          value: type.water_meter_type_id,
          label: type.water_meter_type_name,
          disabled: !project
            ? false
            : typeInProject.includes(type.water_meter_type_id)
            ? false
            : true,
        });
      });
    return typeSelect;
  };

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="xl:grid-col-4 mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-6">
        {!dashboardDataIsLoading && dashboardDataStatus === "success" ? (
          <>
            <Widget
              icon={<MdViewList className="h-7 w-7" />}
              title={"پروژه ها"}
              subtitle={`${dashboardData?.data?.water_meter_project} عدد`}
              link={"/admin/projects"}
            />
            <Widget
              icon={<MdGasMeter className="h-6 w-6" />}
              title={"دستگاه ها"}
              subtitle={`${dashboardData?.data?.all_water_meter} عدد`}
              link={"/admin/counters"}
            />
            <Widget
              icon={<MdPeopleAlt className="h-7 w-7" />}
              title={"بهره برداران"}
              subtitle={`${dashboardData?.data?.all_users} نفر`}
              link={"/admin/users"}
            />
            <Widget
              icon={<MdCategory className="h-7 w-7" />}
              title={"انواع دستگاه ها"}
              subtitle={`${dashboardData?.data?.water_meter_type} نوع`}
              link={null}
            />
            <Widget
              icon={<MdCategory className="h-7 w-7" />}
              title={"تعداد دستگاه فعال"}
              subtitle={`${dashboardData?.data?.water_meter_activation} عدد`}
              link={null}
            />
          </>
        ) : (
          <Loading />
        )}
      </div>

      {/* ===== ANALYTICS BOARD (fits one screen) ===== */}
      {!citiesIsLoading &&
      citiesStatus &&
      !devicesIsLoading &&
      devicesStatus === "success" ? (
        <div
          className="
      /* <— tune this
      if   your navbar/footer are different */ grid grid-cols-1 gap-4 overflow-hidden xl:h-[86vh]
      xl:grid-cols-12
    "
        >
          {/* LEFT SIDE: Donuts (top) + Chart (bottom) */}
          <div className="order-1 xl:col-span-7">
            <div className="grid h-full grid-cols-1 gap-4">
              {/* Donuts row (two boxes side by side) */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-white p-3 shadow-sm dark:bg-navy-800">
                  <div className="mb-2 text-base font-semibold dark:text-white">
                    تفکیک دستگاه ها
                  </div>
                  <div className="h-[22vh]">
                    <DonutChart
                      chartOptions={renderDonutOptions(devicesData?.data)}
                      chartData={renderDonutData(devicesData?.data)}
                    />
                  </div>
                </div>

                <div className="rounded-2xl bg-white p-3 shadow-sm dark:bg-navy-800">
                  <div className="mb-2 text-base font-semibold dark:text-white">
                    پروژه به تفکیک شهر
                  </div>
                  <div className="h-[22vh]">
                    <PieChart
                      chartOptions={renderChartOptions(citiesData?.data)}
                      chartData={renderChartData(citiesData?.data)}
                    />
                  </div>
                </div>
              </div>

              {/* Chart under donuts */}
              <div className="rounded-2xl bg-white p-3 shadow-sm dark:bg-navy-800">
                <div className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
                  مصرف ماه اخیر
                </div>

                {/* Filters inline (compact) */}
                {!usersIsLoading &&
                usersStatus === "success" &&
                !projectsIsLoading &&
                projectsStatus === "success" &&
                !typesIsLoading &&
                typesStatus === "success" ? (
                  <div className="-mt-1 mb-2">
                    <ConsumptionChartFilter
                      tagSelect={counterTagSelect}
                      tag={tag}
                      setTag={setTag}
                      userSelect={renderSelectData(usersData.data, "user")}
                      user={user}
                      setUser={setUser}
                      projectSelect={renderSelectData(
                        projectsData.data,
                        "project"
                      )}
                      project={project}
                      setProject={setProject}
                      typeSelect={renderTypeSelect(typesData.data)}
                      type={type}
                      setType={setType}
                    />
                  </div>
                ) : (
                  <Loading />
                )}

                {/* meter selection row (tiny, single line) */}
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="text-gray-600 dark:text-gray-300">
                    {selectedMeters.length
                      ? `${selectedMeters.length} کنتور انتخاب شده`
                      : "هیچ کنتوری انتخاب نشده است"}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMetersOpen(true)}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700"
                    >
                      انتخاب کنتورها
                    </button>
                    {selectedMeters.length > 0 && (
                      <button
                        onClick={() => {
                          setSelectedMeters([]);
                          consumptionDatesRefetch();
                        }}
                        className="rounded-lg border border-gray-300 px-3 py-1.5 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-navy-700"
                      >
                        پاک‌سازی
                      </button>
                    )}
                  </div>
                </div>

                {/* Chart — sized to fit below donuts within viewport */}
                {!consumptionsDatesIsLoading &&
                consumptionsDatesStatus === "success" ? (
                  <div className="h-[34vh]">
                    <MixChart
                      chartData={renderComplexChartData(
                        consumptionsDatesData?.data,
                        fromDate,
                        tillDate
                      )}
                      chartOptions={renderComplexChartOptions(
                        fromDate,
                        tillDate
                      )}
                      chartTag={tag}
                    />
                  </div>
                ) : (
                  <div className="h-[34vh]">
                    <Loading />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Map (narrower) */}
          <div className="order-2 xl:col-span-5">
            <div className="h-full rounded-2xl bg-white p-3 shadow-sm dark:bg-navy-800">
              <div className="mb-3 text-lg font-semibold dark:text-white">
                نقشه پروژه‌ها
              </div>
              <div className="h-[calc(86vh-4rem)] w-full overflow-hidden rounded-xl">
                {/* 86vh matches the board height; 4rem ≈ header inside this card */}
                <CustomMap />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Loading />
      )}

      {/* Modal stays unchanged */}
      <WaterMetersPicker
        isOpen={metersOpen}
        onClose={() => setMetersOpen(false)}
        options={meterOptions}
        value={selectedMeters}
        onChange={setSelectedMeters}
        onApply={consumptionDatesRefetch}
      />
    </div>
  );
};

export default Dashboard;
